// Textarea — multi-line text input per COMPONENTS §Catalog + §Input + §Per-
// primitive WCAG. The multi-line sibling of <Input>: same composition (page
// stacks <Label> + <Textarea> + error), same aria wiring (aria-invalid +
// aria-describedby when `error` present), same token styling.
//
// Plain string value → integrate with RHF via `register` (like Input), not
// Controller. Validation is form-state-level (RHF + Zod resolver per ADR-008);
// the `error` prop just renders the visual message.

import { useId, forwardRef, type TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Error message displayed below; sets aria-invalid + aria-describedby. */
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { error, id: idProp, rows = 4, className = '', ...props },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? reactId;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1">
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={
          // Border + hover + focus light-up + invalid: the shared `field-frame`
          // utility (DESIGN-TOKENS §Interaction states); aria-invalid drives the
          // error border. Never re-inline the recipe.
          // pointer-coarse:text-base — iOS input-zoom guard (canonical note: Input.tsx).
          'w-full px-3 py-2 text-sm pointer-coarse:text-base rounded-md outline-none transition-colors resize-y ' +
          'bg-[var(--color-bg-page)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] ' +
          'disabled:bg-[var(--color-bg-surface-alt)] disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed ' +
          'read-only:bg-[var(--color-bg-surface-alt)] ' +
          `field-frame ${className}`
        }
        {...props}
      />
      {error && (
        <p id={errorId} className="text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
});
